import { post } from "../../fetch-interceptor";
import { baseUrl } from "./common";

type Subscriber = (url: string | null) => void;

const cache = new Map<string, string | null>();
const pending = new Map<string, Set<Subscriber>>();
const persistedReadsInProgress = new Set<string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

const FLUSH_DELAY_MS = 500;
const CHUNK_SIZE = 100;
const STORAGE_TTL_MS = 24 * 60 * 60 * 1000;
const CLEANUP_INTERVAL_MS = 60 * 60 * 1000;

const DB_NAME = "ppm-express-image-cache";
const DB_VERSION = 1;
const STORE_NAME = "images";
const TIMESTAMP_INDEX = "timestamp";

let dbPromise: Promise<IDBDatabase | null> | null = null;
let lastCleanupAt = 0;

type PersistedImage = {
    id: string;
    url: string;
    timestamp: number;
};

export function loadImage(imageId: string, callback: Subscriber): () => void {
    const cached = getFromCache(imageId);
    if (cached !== undefined) {
        callback(cached);
        return () => {};
    }

    if (!pending.has(imageId)) {
        pending.set(imageId, new Set());
    }
    pending.get(imageId)!.add(callback);

    tryHydrateFromPersistence(imageId);

    return () => {
        const subscribers = pending.get(imageId);
        if (!subscribers) {
            return;
        }

        subscribers.delete(callback);
        if (subscribers.size === 0) {
            pending.delete(imageId);
        }
    };
}

export function getCachedImage(imageId: string): string | null | undefined {
    return getFromCache(imageId);
}

async function flush() {
    flushTimer = null;
    const ids = [...pending.keys()].filter(_ => !persistedReadsInProgress.has(_) && !cache.has(_));
    if (!ids.length) {
        return;
    }

    for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
        const chunk = ids.slice(i, i + CHUNK_SIZE);

        try {
            const response = await post<Record<string, string | null>>(
                `${baseUrl.value}/api/image/batch`,
                chunk
            );

            for (const id of chunk) {
                const dataUrl = response[id] ?? null;
                setCached(id, dataUrl);
                notifyAndClear(id, dataUrl);
            }
        } catch {
            for (const id of chunk) {
                notifyAndClear(id, null);
            }
        }
    }
}

function getFromCache(imageId: string): string | null | undefined {
    if (cache.has(imageId)) {
        return cache.get(imageId)!;
    }

    return undefined;
}

function setCached(imageId: string, dataUrl: string | null) {
    cache.set(imageId, dataUrl);

    if (dataUrl) {
        void persistImage(imageId, dataUrl);
    }
}

async function persistImage(imageId: string, dataUrl: string): Promise<void> {
    const db = await openDb();
    if (!db) {
        return;
    }

    try {
        const transaction = db.transaction(STORE_NAME, "readwrite");
        const store = transaction.objectStore(STORE_NAME);

        store.put({ id: imageId, url: dataUrl, timestamp: Date.now() } as PersistedImage);
        await cleanupExpiredImages(store);
    } catch {
        // ignore persistence failures/quota issues
    }
}

function tryHydrateFromPersistence(imageId: string): void {
    if (persistedReadsInProgress.has(imageId)) {
        return;
    }

    persistedReadsInProgress.add(imageId);

    void readPersistedImage(imageId)
        .then(persisted => {
            if (persisted !== null) {
                cache.set(imageId, persisted);
                notifyAndClear(imageId, persisted);
                return;
            }

            if (pending.has(imageId)) {
                scheduleFlush();
            }
        })
        .catch(() => {
            if (pending.has(imageId)) {
                scheduleFlush();
            }
        })
        .finally(() => {
            persistedReadsInProgress.delete(imageId);
        });
}

function notifyAndClear(imageId: string, dataUrl: string | null): void {
    pending.get(imageId)?.forEach(cb => cb(dataUrl));
    pending.delete(imageId);
}

function scheduleFlush(): void {
    if (!flushTimer) {
        flushTimer = setTimeout(flush, FLUSH_DELAY_MS);
    }
}

async function readPersistedImage(imageId: string): Promise<string | null> {
    const db = await openDb();
    if (!db) {
        return null;
    }

    try {
        const transaction = db.transaction(STORE_NAME, "readwrite");
        const store = transaction.objectStore(STORE_NAME);
        const record = await requestToPromise(store.get(imageId) as IDBRequest<PersistedImage | undefined>);
        if (!record?.url || !record?.timestamp || Date.now() - record.timestamp > STORAGE_TTL_MS) {
            if (record) {
                store.delete(imageId);
            }

            return null;
        }

        store.put({ ...record, timestamp: Date.now() });
        return record.url;
    } catch {
        return null;
    }
}

async function cleanupExpiredImages(store: IDBObjectStore): Promise<void> {
    const now = Date.now();
    if (now - lastCleanupAt < CLEANUP_INTERVAL_MS) {
        return;
    }

    lastCleanupAt = now;

    try {
        const timestampIndex = store.index(TIMESTAMP_INDEX);
        const expiryThreshold = now - STORAGE_TTL_MS;

        await new Promise<void>(resolve => {
            const range = IDBKeyRange.upperBound(expiryThreshold);
            const cursorRequest = timestampIndex.openCursor(range);

            cursorRequest.onsuccess = () => {
                const cursor = cursorRequest.result;
                if (!cursor) {
                    resolve();
                    return;
                }

                store.delete(cursor.primaryKey);
                cursor.continue();
            };

            cursorRequest.onerror = () => resolve();
        });
    } catch {
        // ignore cleanup failures
    }
}

function openDb(): Promise<IDBDatabase | null> {
    if (dbPromise) {
        return dbPromise;
    }

    if (typeof indexedDB === "undefined") {
        dbPromise = Promise.resolve(null);
        return dbPromise;
    }

    dbPromise = new Promise(resolve => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = () => {
            const db = request.result;
            const store = db.objectStoreNames.contains(STORE_NAME)
                ? request.transaction!.objectStore(STORE_NAME)
                : db.createObjectStore(STORE_NAME, { keyPath: "id" });

            if (!store.indexNames.contains(TIMESTAMP_INDEX)) {
                store.createIndex(TIMESTAMP_INDEX, "timestamp", { unique: false });
            }
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(null);
        request.onblocked = () => resolve(null);
    });

    return dbPromise;
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
    });
}